import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';

import FileImport from '../../assets/FileImport.svg?react';
import SetupBubbles2 from '../../assets/SetupBubbles2.png';
import SetupProjectIcon from '../../assets/SetupProjectIcon.svg?react';
import { useModelContext } from '../../context/modelContext';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { usePedestrianLayerTypeEnabled } from '../../hooks/useIdlc1129Features';
import { projectsReducer } from '../../store/reducers/projects.reducer';
import { getNewProjectSelector } from '../../store/selectors/projects.selector';
import { getUser } from '../../store/selectors/user.selector.ts';
import { history } from '../../store/store.ts';
import { DownloadProgress, FileType } from '../../types/file';
import { inferContentType } from '../../utils/contentTypeUtils.ts';
import { mapModelLayersToSettings } from '../../utils/layerUtils';
import { FileDownloadProgress } from '../loading/FileDownloadProgress';
import { FileUploadDialog } from '../setup-project/components/FileUploadDialog';
import { OnboardingPopup } from '../setup-project/components/OnboardingPopup';
import { ProjectDetails } from '../setup-project/components/ProjectDetails.tsx';
import { EXAMPLE_MODELS } from '../setup-project/constants/modelConstants';
import { useOnboarding } from '../setup-project/hooks/useOnboarding';
import { useValidation } from '../setup-project/hooks/useValidation';
import { SetupProjectProps } from '../setup-project/types';
import { getFileIcon } from '../setup-project/utils/fileHelpers';
import { ModelScaleSummaryRow } from './ModelScaleSummaryRow';
import { DXFImportDialog } from './dxf/DXFImportDialog';
import { DXFData } from './dxf/types';

const { setNewProject, setModels } = projectsReducer.actions;

export default function SetupProject({ onNext, onOpenScaleBar, areModelsLoaded = true }: SetupProjectProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();

  const project = useAppSelector(getNewProjectSelector);
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();
  const defaultSettings = useAppSelector(getUser)?.settings;

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && defaultSettings && !project.units) {
      hasInitialized.current = true;
      dispatch(
        setNewProject({
          ...project,
          units: defaultSettings.units,
          tolerance: defaultSettings.tolerance,
          maintenance: defaultSettings.maintenance,
        }),
      );
    }
  }, [dispatch, defaultSettings, project]);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<Array<DownloadProgress>>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const { uploadedModels, setUploadedModels, setModelLayers, modelLayers } = useModelContext();
  const { errors, touched, validateForm, handleBlur } = useValidation(project);
  const { showOnboarding, closeOnboarding } = useOnboarding();

  // DXF import state
  const [dxfData, setDxfData] = useState<DXFData | null>(null);
  const [dxfFileName] = useState('');
  const [dxfDialogOpen, setDxfDialogOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [validateForm]);

  const handleInputChange = useCallback(
    (field: keyof typeof project) =>
      (event: React.ChangeEvent<HTMLInputElement | { value: unknown }> | SelectChangeEvent) => {
        const value = (event.target as HTMLInputElement).value ?? (event.target as { value: string }).value;
        dispatch(setNewProject({ ...project, [field]: value }));
      },
    [dispatch, project],
  );

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    await handleFileSelect({
      target: { files },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleFileSelect(e);
  };

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
      const maxSize = 1024 * 1024 * 1024; // 1GB in bytes

      if (totalSize > maxSize) {
        setUploadError(
          `Total file size exceeds the maximum limit of 1GB. Current total: ${(totalSize / (1024 * 1024)).toFixed(2)}MB`,
        );
        return;
      }

      setIsUploading(true);

      try {
        // Wait for file processing to complete
        const modelFiles = await Promise.all(
          Array.from(files).map(async (file) => {
            // Simulate file processing/validation time
            await new Promise((resolve) => setTimeout(resolve, 100));

            return {
              name: file.name,
              size: file.size,
              type: file.type || inferContentType(file.name),
              lastModified: file.lastModified,
              url: URL.createObjectURL(file),
            };
          }),
        );

        // Only update state after file processing is complete
        const updatedModels = uploadedModels ? [...uploadedModels, ...modelFiles] : modelFiles;
        setUploadedModels(updatedModels);
        dispatch(setModels(updatedModels));

        setUploadDialogOpen(false);
      } catch (_error) {
        setUploadError('Failed to process uploaded files');
      } finally {
        setIsUploading(false);
      }
    },
    [dispatch, setUploadError, uploadedModels, setUploadedModels],
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (uploadedModels) {
        const newFiles = [...uploadedModels];
        const removedFile = newFiles[index];

        if (removedFile.url) {
          URL.revokeObjectURL(removedFile.url);
        }

        newFiles.splice(index, 1);

        if (newFiles.length === 0) {
          setUploadedModels(null);
          setModelLayers([]);
        } else {
          setUploadedModels(newFiles);
          // Remove layers belonging to the deleted model
          setModelLayers((prev) => prev.filter((layer) => layer.modelName !== removedFile.name));
        }
      }
    },
    [uploadedModels, setUploadedModels, setModelLayers],
  );

  const handleExampleModelSelect = useCallback(
    async (url: string, filename: string) => {
      try {
        setIsUploading(true);
        setIsDownloading(true);
        setUploadError(null);

        const model = EXAMPLE_MODELS.find((m) => m.filename === filename);
        const fileId = `${filename}-${Date.now()}`;

        // Initialize progress tracking
        setDownloadProgress([
          {
            fileId,
            fileName: filename,
            fileType: FileType.OBJECT_3D,
            totalSize: model?.size ?? 0,
            downloadedSize: 0,
            isComplete: false,
          },
        ]);

        // Stream download to report progress similar to the old flow
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Failed to get response reader');
        }

        const contentLength = response.headers.get('content-length');
        const totalSize = contentLength ? parseInt(contentLength, 10) : (model?.size ?? 0);

        let downloadedSize = 0;
        const chunks: ArrayBuffer[] = [];
        let done = false;
        while (!done) {
          const result = await reader.read();
          done = result.done ?? false;
          if (!done && result.value) {
            const chunk = result.value;
            downloadedSize += chunk.byteLength;
            chunks.push(chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength));
            setDownloadProgress((prev) =>
              prev.map((item) => (item.fileId === fileId ? { ...item, downloadedSize } : item)),
            );
          }
        }

        const blob = new Blob(chunks);
        const objectUrl = URL.createObjectURL(blob);

        setDownloadProgress((prev) =>
          prev.map((item) =>
            item.fileId === fileId ? { ...item, downloadedSize: totalSize, isComplete: true } : item,
          ),
        );

        const modelFile = {
          name: filename,
          size: blob.size,
          type: inferContentType(filename),
          lastModified: Date.now(),
          url: objectUrl,
        };

        const updatedModels = [modelFile];
        // Clear old layers before replacing models (example replaces all)
        setModelLayers([]);
        setUploadedModels(updatedModels);
        dispatch(setModels(updatedModels));
        setUploadDialogOpen(false);
      } catch (_error) {
        setUploadError(`Failed to download ${filename}. Please try again.`);
        setDownloadProgress([]);
      } finally {
        setIsDownloading(false);
        setIsUploading(false);
      }
    },
    [setUploadedModels, dispatch, setUploadError, setModelLayers],
  );

  const handleNextClick = useCallback(() => {
    if (!areModelsLoaded) return;
    if (validateForm()) {
      // Calculate layer settings from model files
      const layerSettings = mapModelLayersToSettings(modelLayers, { pedestrian: pedestrianLayerType });

      // Update project with calculated layer settings
      dispatch(
        setNewProject({
          ...project,
          layers: layerSettings,
        }),
      );

      onNext?.();
    }
  }, [areModelsLoaded, validateForm, onNext, modelLayers, dispatch, project, pedestrianLayerType]);

  const handleCancel = () => {
    history.push('/projects');
  };

  const handleDxfGenerate = useCallback(
    (glbBlob: Blob, fileName: string) => {
      const objectUrl = URL.createObjectURL(glbBlob);
      const modelFile = {
        name: fileName,
        size: glbBlob.size,
        type: 'model/gltf-binary',
        lastModified: Date.now(),
        url: objectUrl,
      };
      const updatedModels = [modelFile];
      setUploadedModels(updatedModels);
      dispatch(setModels(updatedModels));
      setDxfDialogOpen(false);
      setDxfData(null);

      // Auto-advance to next step if form is valid and models are loaded
      if (areModelsLoaded && validateForm()) {
        const layerSettings = mapModelLayersToSettings(modelLayers, { pedestrian: pedestrianLayerType });
        dispatch(setNewProject({ ...project, layers: layerSettings }));
        onNext?.();
      }
    },
    [dispatch, setUploadedModels, validateForm, modelLayers, project, onNext, areModelsLoaded, pedestrianLayerType],
  );

  const hasFiles = (uploadedModels?.length ?? 0) > 0;

  const formatFileName = (name: string) => {
    return name.length > 30 ? name.substring(0, 30) + '...' : name;
  };

  return (
    <div
      className="fixed top-[64px] bottom-0 right-0 h-[calc(100vh-64px)] w-full md:w-[455px] md:rounded-l-2xl text-gray-300 flex flex-col overflow-y-auto bg-black shadow-2xl pointer-events-auto"
      style={{ zIndex: 100 }}
    >
      <div className="flex-shrink-0 bg-[#232323] pt-4 md:pt-0">
        <div className="h-[55px] flex items-center px-6 gap-3 relative overflow-hidden">
          <div className="p-2 rounded-[10px] bg-[#313131] flex-shrink-0">
            <SetupProjectIcon className="w-4 h-4 text-lime-400" strokeWidth={2} />
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-white truncate">{t('projects.newProject.setupTitle')}</h1>
          <img
            src={SetupBubbles2}
            alt="dots"
            className="absolute -top-[320px] -right-[200px] rotate-90 pointer-events-none select-none hidden md:block"
            style={{
              WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 60%)',
              maskImage: 'linear-gradient(to top, transparent 0%, black 60%)',
            }}
          />
        </div>

        <div className="mt-3 px-6 pb-4">
          <input
            type="text"
            placeholder={t('projects.newProject.projectNamePlaceholder')}
            value={project.name ?? ''}
            onChange={handleInputChange('name')}
            onBlur={handleBlur('name')}
            className={`w-full h-[36px] rounded-lg bg-black border placeholder-[#747474] text-sm px-4 text-white transition outline-none
        ${
          touched.name && errors.name
            ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/30'
            : 'border-black focus:border-lime-400 focus:ring-2 focus:ring-lime-400/30'
        }`}
          />

          {touched.name && errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>
      </div>

      <div className="bg-[#232323] overflow-y-auto flex-1">
        <div className="pb-8">
          <div className="mt-4 px-6">
            <input
              type="file"
              accept=".glb,.obj"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />

            <label
              onClick={handleFileBrowseClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`block border-2 border-dashed rounded-xl flex items-center justify-center gap-3 cursor-pointer transition 
          ${isDragging ? 'border-lime-400 bg-lime-400/5' : 'border-black hover:border-lime-400/50'}
          ${hasFiles ? 'h-auto py-4 sm:h-[70px] flex-col sm:flex-row' : 'h-[150px] flex-col'} 
        `}
            >
              <FileImport className={`${hasFiles ? 'w-6 h-6' : 'w-10 h-10'} text-lime-400`} />

              <div className={`${hasFiles ? 'text-left' : 'text-center'}`}>
                <p className="text-sm text-white font-medium">
                  {hasFiles ? t('projects.newProject.importAnother') : t('projects.newProject.importHint')}
                </p>
                {!hasFiles && <p className="text-xs text-gray-400">{t('projects.newProject.supportedFiles')}</p>}
              </div>
            </label>

            {/* LISTA FILEOVA */}
            {hasFiles && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                  {t('projects.newProject.uploadedFiles', { count: uploadedModels!.length })}
                </p>

                {/* Ovdje max-height i scroll kontroliraju visinu */}
                <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                  {uploadedModels!.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between bg-[#313131] p-2 px-3 rounded-lg border border-gray-900 shrink-0"
                    >
                      {/* OVDJE JE PROMJENA: Sve je u jednom redu (flex container) */}
                      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                        {/* Ime file-a */}
                        <span className="text-sm text-white truncate max-w-[150px] sm:max-w-none" title={file.name}>
                          {formatFileName(file.name)}
                        </span>

                        {/* Veličina u istom redu, s 'whitespace-nowrap' da se ne prelomi */}
                        <span className="text-xs text-gray-500 whitespace-nowrap shrink-0">
                          ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        className="text-gray-500 hover:text-red-500 transition p-1 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex w-full">
            <ProjectDetails
              project={project}
              errors={errors}
              touched={touched}
              onInputChange={handleInputChange}
              onBlur={handleBlur}
            />
          </div>

          <ModelScaleSummaryRow onEdit={() => onOpenScaleBar?.()} disabled={!hasFiles} />

          <div className="mt-6 px-6 w-full text-white">
            <h3 className="text-sm uppercase tracking-wider mb-3">{t('projects.newProject.examples')}</h3>

            <div className="flex flex-wrap justify-center sm:justify-start gap-4">
              {EXAMPLE_MODELS.map((example) => (
                <div key={example.filename} className="flex flex-col items-center">
                  <button
                    onClick={() => handleExampleModelSelect(example.url, example.filename)}
                    className={`w-[80px] aspect-square rounded-lg border transition hover:scale-105 ${
                      uploadedModels?.some((m) => m.name === example.filename)
                        ? 'border-lime-400'
                        : 'border-black hover:border-lime-400/50'
                    }`}
                    disabled={isUploading}
                  >
                    <div className="flex flex-col items-center justify-center h-full gap-2">
                      {/* Ikona fajla */}
                      <div className="w-10 h-10 opacity-80 flex items-center justify-center">
                        {getFileIcon(example.filename)}
                      </div>
                    </div>
                  </button>

                  <p className="w-[80px] text-xs text-gray-300 text-center mt-2 px-2 whitespace-pre-line">
                    {example.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-bl-2xl bg-[#232323] bottom-0 right-0 w-full h-[60px] backdrop-blur-sm flex justify-between px-6 items-center gap-4 border-t border-gray-800">
        <button
          key="cancel"
          onClick={handleCancel}
          className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs ${'border border-[#B3EC49] text-[#B3EC49]  hover:border-lime-500'}`}
          style={{
            height: '36px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '18px',
            paddingRight: '18px',
            gap: '8px',
          }}
        >
          {t('common.cancel')}
        </button>

        <button
          key="next-step"
          onClick={handleNextClick}
          disabled={!uploadedModels || uploadedModels.length === 0 || !isFormValid || !areModelsLoaded}
          title={!areModelsLoaded ? (t('projects.newProject.waitForModelLoading') ?? '') : undefined}
          className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs ${'border border-gray-600 hover:border-gray-500 bg-lime-400 text-black font-medium hover:bg-lime-500 transition disabled:opacity-50 disabled:cursor-not-allowed'}`}
          style={{
            height: '36px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '18px',
            paddingRight: '18px',
            gap: '8px',
          }}
        >
          {t('projects.newProject.nextStep')}
        </button>
      </div>

      <FileDownloadProgress fileDownloadProgress={downloadProgress} isLoadingFiles={isDownloading} />

      <FileUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onFileSelect={handleFileSelect}
        onExampleModelSelect={handleExampleModelSelect}
        uploadError={uploadError}
        onClearError={() => setUploadError(null)}
        isMobile={isMobile}
      />

      <OnboardingPopup isOpen={showOnboarding} onClose={closeOnboarding} />

      {dxfData && (
        <DXFImportDialog
          open={dxfDialogOpen}
          onClose={() => setDxfDialogOpen(false)}
          parsedData={dxfData}
          onGenerate={handleDxfGenerate}
          originalFileName={dxfFileName}
        />
      )}
    </div>
  );
}
